import React, { useState, useEffect } from "react";
import SeoHead from "../components/SeoHead";
import GeneralContactForm from "../components/GeneralContactForm";
import GalleryLightbox from "../components/GalleryLightbox";
import CleaningPricingTable from "../components/CleaningPricingTable";
import { BUSINESS_INFO, IMAGES, GALLERIES, VERBATIM_TEXTS, PRODUCTS_AND_EQUIPMENT } from "../data";

export default function Sommerhusrengoering() {
  const [galleryImages, setGalleryImages] = useState(GALLERIES[0].images);

  useEffect(() => {
    // Attempt fetching gallery from CMS
    fetch("/_api/public/v2/content/gallery?slug=sommerhusrengoering")
      .then((res) => res.json())
      .then((data) => {
        if (data?.items?.[0]?.images && Array.isArray(data.items[0].images) && data.items[0].images.length > 0) {
          setGalleryImages(data.items[0].images);
        }
      })
      .catch(() => {
        // Fallback to initial mock data
      });
  }, []);

  return (
    <>
      <SeoHead
        title="Sommerhusrengøring"
        description="Effektiv og grundig sommerhusrengøring i Norddjurs med Ses Multi Service. Vi aftørrer flader, støvsuger, vasker gulv og tjekker for fejl og mangler."
        path="/sommerhusrengoering"
      />

      {/* SUBPAGE HERO */}
      <section className="bg-gradient-to-b from-[#F4EFE6] to-[#FAF7F2] py-12 lg:py-20 border-b border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 bg-[#EADFCF] text-[#4B3C2D] px-3.5 py-1 rounded-full text-xs font-semibold">
                <i className="fa-solid fa-house-chimney text-[#887055]" aria-hidden="true"></i>
                <span>Ydelse i Norddjurs</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading text-[#2C2523]">
                {VERBATIM_TEXTS.sommerhus.heading}
              </h1>

              <p className="text-base text-[#5C524C] leading-relaxed max-w-2xl">
                Professionel rengøring af dit sommerhus, så det altid er klar og indbydende til ejere og lejegæster.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <a
                  href={`tel:${BUSINESS_INFO.phone}`}
                  className="bg-[#3A302B] hover:bg-[#231C19] text-[#FFFDF9] px-6 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">Ring 40 94 75 40</span>
                </a>
                <a
                  href="#bestil-form"
                  className="bg-[#EADFCF] hover:bg-[#D8C7B0] text-[#2C2523] px-6 py-3 rounded-xl font-semibold text-sm transition-colors border border-[#D8C7B0]"
                >
                  Få et uforpligtende tilbud
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-[#EADFCF] shadow-lg">
                <img
                  src={IMAGES.clientReal1}
                  alt="Rengjort sommerhus i Norddjurs af Ses Multi Service"
                  className="w-full h-[320px] object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* VERBATIM CUSTOMER COPY SECTION */}
      <section className="py-14 lg:py-20 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="sand-card-light rounded-3xl p-8 sm:p-12 border border-[#EADFCF] shadow-xs space-y-6">
            <div className="flex items-center gap-3 border-b border-[#EADFCF] pb-4">
              <div className="w-10 h-10 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-lg">
                <i className="fa-solid fa-sparkles" aria-hidden="true"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold font-heading text-[#2C2523]">
                  Rengøring af sommerhus
                </h2>
                <p className="text-xs text-[#887055] font-medium">Ses Multi Service i Norddjurs</p>
              </div>
            </div>

            {/* EXACT VERBATIM TEXT INSERTION */}
            <div className="text-base text-[#2C2523] leading-relaxed space-y-4 font-normal">
              <p>
                Oplev grundigt sommerhus rengøring med Ses Multi Service, hvor du får aftørret alle flader, støvsuget og vasket gulv.
              </p>
              <p>
                Vi tjekker fejl og mangler, og kan sende billeder, hvis noget skulle være i stykker.
              </p>
              <p className="font-semibold text-[#3A302B]">
                Vores mål er effektiv rengøring, hver gang – for at sikre din tilfredshed og et skinnending rent sommerhus!
              </p>
            </div>

            {/* Key feature list */}
            <div className="pt-6 border-t border-[#EADFCF] grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-[#5C524C]">
              <div className="bg-[#FAF7F2] p-3 rounded-xl border border-[#EADFCF] flex items-center gap-2">
                <i className="fa-solid fa-broom text-[#887055]" aria-hidden="true"></i>
                <span>Aftørring, støvsugning & vask</span>
              </div>
              <div className="bg-[#FAF7F2] p-3 rounded-xl border border-[#EADFCF] flex items-center gap-2">
                <i className="fa-solid fa-magnifying-glass text-[#887055]" aria-hidden="true"></i>
                <span>Tjek for fejl og mangler</span>
              </div>
              <div className="bg-[#FAF7F2] p-3 rounded-xl border border-[#EADFCF] flex items-center gap-2">
                <i className="fa-solid fa-camera text-[#887055]" aria-hidden="true"></i>
                <span>Fotodokumentation</span>
              </div>
            </div>

            {/* Tidsbestilling punkter */}
            <div className="p-4 bg-[#FAF7F2] rounded-2xl border border-[#EADFCF] space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[#887055] block">
                Bestilling af rengøring:
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-[#2C2523] font-medium">
                <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-[#EADFCF]">
                  <i className="fa-solid fa-calendar-day text-[#887055]" aria-hidden="true"></i>
                  <span>Mandag til fredag</span>
                </div>
                <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-[#EADFCF]">
                  <i className="fa-solid fa-clock text-[#887055]" aria-hidden="true"></i>
                  <span>Mellem 9 og 15</span>
                </div>
                <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-[#EADFCF]">
                  <i className="fa-solid fa-calendar-days text-[#887055]" aria-hidden="true"></i>
                  <span>Sep okt nov og dec</span>
                </div>
              </div>
            </div>

            {/* Products & Equipment Box */}
            <div className="pt-6 border-t border-[#EADFCF] space-y-4">
              <div className="bg-[#FAF7F2] p-6 rounded-2xl border border-[#EADFCF] space-y-4">
                <div className="flex items-center gap-2.5 text-[#2C2523] font-bold font-heading">
                  <i className="fa-solid fa-leaf text-[#887055]"></i>
                  <span>Udstyr & miljøvenlige produkter</span>
                </div>
                <div className="text-sm text-[#4A403A] space-y-2">
                  <p>{PRODUCTS_AND_EQUIPMENT.intro}</p>
                  <p className="text-xs sm:text-sm text-[#5C524C]">{PRODUCTS_AND_EQUIPMENT.subIntro}</p>
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#887055] block mb-2">
                    {PRODUCTS_AND_EQUIPMENT.cleaningHeading}
                  </span>
                  <div className="flex flex-wrap gap-3">
                    {PRODUCTS_AND_EQUIPMENT.cleaningLinks.map((item, idx) => (
                      <a
                        key={idx}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold text-[#2C2523] bg-white hover:bg-[#FFFDF9] px-3.5 py-2 rounded-lg border border-[#D8C7B0] hover:border-[#887055] transition-colors shadow-2xs group"
                      >
                        <span>{item.url}</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-[#887055]" aria-hidden="true"></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* GALLERY SECTION */}
      <section className="py-14 lg:py-20 bg-[#F4EFE6] border-y border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GalleryLightbox
            title="Galleri – Sommerhusrengøring"
            description="Se eksempler på vores grundige rengøring i moderne sommerhuse i Norddjurs."
            images={galleryImages}
          />
        </div>
      </section>

      {/* PRICING SECTION */}
      <CleaningPricingTable />

      {/* CONTACT FORM SECTION */}
      <section id="bestil-form" className="py-16 lg:py-24 bg-[#FAF7F2] border-t border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GeneralContactForm defaultService="Sommerhusrengøring" />
        </div>
      </section>
    </>
  );
}
